import { Autocomplete, Stack, styled } from '@mui/material';

import { BaseMultiAutocompleteProps } from '../types';
import AutocompleteInput from './AutocompleteInput';
import AutocompleteLabel from './AutocompleteLabel';

interface StyledAutocompleteProps {
  width?: string | number;
}

const StyledAutocomplete = styled(Autocomplete)<StyledAutocompleteProps>({
  fontSize: 14,
  margin: 0,
  '& .MuiOutlinedInput-root': {
    flexWrap: 'wrap',
    minHeight: '36px',
    height: 'auto',
  },
  '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
    paddingRight: '0 !important',
  },
}) as typeof Autocomplete;

function AutocompleteWithLabel<T>(props: BaseMultiAutocompleteProps<T>['autocomplete']) {
  return (
    <Stack spacing='4px'>
      {props.label && <AutocompleteLabel label={props.label ?? 'Filter'} />}
      <StyledAutocomplete<T, true, false, false>
        freeSolo={false}
        /* NOTE: In general avoid using "sx", should be do it ocassionally when we need individual changes.
        We should aim for using styled utility only, but I was not able to find proper way of passing
        width prop here without breaking generics and type-safety of the component. Please note this is very specific case
        */
        sx={{ width: props.width ?? '360px' }}
        data-testid={props.testIdSuffix ? `base-multi-autocomplete-${props.testIdSuffix}` : 'base-multi-autocomplete'}
        options={props.options}
        value={props.value}
        inputValue={props.inputValue}
        filterOptions={props.filterOptions}
        multiple={true}
        size='small'
        limitTags={1}
        disabled={props.disabled ?? false}
        slotProps={{
          listbox: {
            sx: {
              fontSize: 14,
              fontWeight: 400,
              padding: 0,
              "& .MuiAutocomplete-option[aria-disabled='true']": {
                backgroundColor: 'transparent !important',
                '&:hover': {
                  backgroundColor: 'transparent !important',
                },
              },
            },
          },
          popper: {
            sx: {
              [`& .MuiAutocomplete-noOptions`]: {
                display: 'none',
              },
            },
          },
        }}
        onInputChange={props.onInputChange}
        getOptionLabel={props.getOptionLabel}
        getOptionKey={props.getOptionKey}
        onChange={props.onChange}
        getOptionDisabled={props.getOptionDisabled}
        isOptionEqualToValue={props.isOptionEqualToValue}
        loading={props.loading ?? false}
        clearOnBlur
        clearOnEscape
        forcePopupIcon={false}
        renderInput={(params) => (
          <AutocompleteInput
            params={params}
            placeholder={props.placeholder ?? 'Enter at least 3 characters to search'}
            testIdSuffix={props.testIdSuffix}
          />
        )}
      />
    </Stack>
  );
}

export default AutocompleteWithLabel;
